import { useReactiveVar } from '@apollo/client';
import { useMutation } from 'hooks/apollo';
import { defaultTextResource, textResourceModalVar } from '../../variables';
import { Dialog, DialogContent } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import AddTextResourceComponent from './AddTextResourceComponent';
import { ADD_OR_UPDATE_TEXT_RESOURCE } from 'features/textResource/mutations';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { getErrors, useValidation } from '@totalsoft/pure-validations-react';
import { textResourceValidator } from 'features/textResource/validators';
import { textResourceListStore } from '../TextResourceListBrowser';
import { useListBrowserUtils } from '@ts-private/list-browser';

const AddTextResourceModal = () => {
  const textResourceModal = useReactiveVar(textResourceModalVar);
  const { store } = useListBrowserUtils(textResourceListStore);

  const [textResourceLens, dirtyInfo, resetTextResourceLens] = useChangeTrackingLens(defaultTextResource);
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const [textResourceListLens, , resetTextResourceListLens] = useChangeTrackingLens([...store.values, textResourceLens |> get]);
  const textResources = textResourceListLens |> get;
  const lastIndex = textResources.length - 1;
  useEffect(() => {
    resetTextResourceListLens([...store.values, textResourceLens |> get]);
  }, [store.values, textResourceLens, resetTextResourceListLens]);

  const [textResourceValidation, validateTextResources] = useValidation(textResourceValidator);
  const errorMessage = textResourceValidation |> getErrors;
  useEffect(() => {
    validateTextResources(textResources);
  }, [textResources, validateTextResources]);

  const close = useCallback(
    (_, reason) => {
      if (reason === 'backdropClick') return true;
      textResourceModalVar({ open: false, saving: false, method: defaultTextResource });
      resetTextResourceLens();
    },
    [resetTextResourceLens]
  );

  const [addOrUpdateTextResource] = useMutation(ADD_OR_UPDATE_TEXT_RESOURCE, {
    onError: () => {
      textResourceModalVar({ ...textResourceModal, saving: false });
    }
  });

  const handleSave = useCallback(() => {
    const textResource = get(textResourceLens);
    textResourceModalVar({ ...textResourceModal, saving: true });
    addOrUpdateTextResource({ variables: { input: textResource } });
  }, [textResourceLens, addOrUpdateTextResource, textResourceModal]);

  return (
    <Dialog open={textResourceModal.open} maxWidth='sm' scroll={'body'} onClose={close} TransitionProps={{ exit: false }}>
      <DialogContent>
        {textResourceModal.open && (
          <AddTextResourceComponent
            isDirty={isDirty}
            textResourceLens={textResourceLens}
            handleSave={handleSave}
            saving={textResourceModal.saving}
            validation={textResourceValidation[lastIndex]}
            errorMessage={errorMessage}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddTextResourceModal;
